// components/system/SystemResourcesBar.tsx
// CPU / MEM / LOAD / P — live system metrics from /api/system/resources.
// Mirrors backend/fastapi/v2/src/components/StatusBar.tsx (CPU/MEM/LOAD/P group only).

import { useEffect, useState } from 'react';
import { useI18n } from '../../i18n';

interface PipelineProc {
  pid: number;
  cpu: number;
  mem_mb: number;
}

interface SystemResources {
  cpu_percent: number;
  cpu_count: number;
  memory_total_gb: number;
  memory_used_gb: number;
  memory_percent: number;
  load_1: number;
  load_5: number;
  load_15: number;
  pipeline_procs?: Record<string, PipelineProc>;
}

const POLL_MS = 5000;

export function SystemResourcesBar() {
  const { t } = useI18n();
  const sr = t.components.systemResources;
  const [res, setRes] = useState<SystemResources | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const fetchOnce = async () => {
      try {
        const r = await fetch('/api/system/resources', {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled && data && !data.error) {
          setRes(data as SystemResources);
        }
      } catch {
        // network blip — keep last good value
      } finally {
        if (!cancelled) {
          timeoutId = setTimeout(fetchOnce, POLL_MS);
        }
      }
    };
    void fetchOnce();
    return () => {
      cancelled = true;
      controller.abort();
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const cpuColour = !res
    ? '#6b7280'
    : res.cpu_percent > 80
    ? '#f87171'
    : res.cpu_percent > 50
    ? '#fbbf24'
    : '#4ade80';

  const memColour = !res
    ? '#6b7280'
    : res.memory_percent > 85
    ? '#f87171'
    : res.memory_percent > 60
    ? '#fbbf24'
    : '#4ade80';

  const procs = res?.pipeline_procs
    ? Object.entries(res.pipeline_procs)
        .map(([name, v]) => ({ name, ...v }))
        .sort((a, b) => b.cpu - a.cpu)
    : [];

  const procColour = (cpu: number) =>
    cpu > 25 ? '#f87171' : cpu > 15 ? '#fbbf24' : '#4ade80';

  const shortName = (name: string) => {
    const n = name.replace(/_\d+$/, '');
    const map: Record<string, string> = {
      fastpathworker: 'fp',
      lbquotews: 'lb',
      filterworker: 'fw',
      rankdetector: 'rd',
      rankaggregator: 'ra',
      executionworker: 'ex',
      momentum_scanner: 'ms',
      rebuildindicators: 'ri',
    };
    return map[n] ?? n.slice(0, 8);
  };

  return (
    <div
      data-testid="system-resources-bar"
      className="flex items-center gap-3 px-3 py-2 bg-gray-900 rounded border border-gray-800"
    >
      {/* CPU */}
      <span
        title={sr.cpuTitle.replace('{0}', res?.cpu_percent.toFixed(1) ?? '--').replace('{1}', String(res?.cpu_count ?? '?'))}
        className="flex items-center gap-1 text-xs font-mono"
      >
        <span className="text-gray-500">{sr.cpu}</span>
        <span style={{ color: cpuColour }}>
          {res ? `${res.cpu_percent.toFixed(0)}%` : '--'}
        </span>
      </span>

      {/* MEM */}
      <span
        title={sr.memTitle.replace('{0}', String(res?.memory_used_gb ?? '--')).replace('{1}', String(res?.memory_total_gb ?? '--'))}
        className="flex items-center gap-1 text-xs font-mono"
      >
        <span className="text-gray-500">{sr.mem}</span>
        <span style={{ color: memColour }}>
          {res ? `${res.memory_percent.toFixed(0)}%` : '--'}
        </span>
      </span>

      {/* LOAD */}
      <span
        title={sr.loadTitle.replace('{0}', String(res?.load_1 ?? '--'))}
        className="flex items-center gap-1 text-xs font-mono"
      >
        <span className="text-gray-500">{sr.load}</span>
        <span className="text-gray-200">{res ? res.load_1.toFixed(1) : '--'}</span>
      </span>

      {/* P (Pipeline CPU mini-bars) */}
      {procs.length > 0 && (
        <span
          className="flex items-center gap-[2px] text-xs font-mono"
          title={sr.pipelineTitle}
        >
          <span className="text-gray-500 mr-1">{sr.pipeline}</span>
          {procs.slice(0, 4).map(p => (
            <span
              key={p.name}
              className="relative group cursor-default"
            >
              <span
                className="inline-block w-[6px] h-[10px] rounded-[1px] align-bottom mr-[1px]"
                style={{
                  backgroundColor: procColour(p.cpu),
                  opacity: Math.min(p.cpu / 30 + 0.2, 1),
                }}
              />
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-50 pointer-events-none">
                <span className="bg-gray-800 border border-gray-700 text-gray-200 text-[10px] px-2 py-1 rounded whitespace-nowrap">
                  {shortName(p.name)} {p.cpu.toFixed(1)}% {p.mem_mb}MB
                </span>
              </span>
            </span>
          ))}
        </span>
      )}
    </div>
  );
}
